import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Phone,
  RefreshCw,
} from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import ExportarDisparoAvulsoModal from '@/components/modals/ExportarDisparoAvulsoModal';

interface DisparoAvulsoLite {
  id: number;
  template_name?: string;
  template_language?: string;
  status?: string;
  total_contatos?: number;
  enviados?: number;
  falhas?: number;
  client_id?: number;
  created_at?: string;
  whatsappConnection?: { name?: string };
  client?: { trade_name?: string; legal_name?: string };
}

interface ContatoLite {
  id: number;
  telefone_destino: string;
  variaveis?: string[];
  status_envio: string;
  enviado_at?: string | null;
  motivo_rejeicao?: string | null;
}

interface DisparoAvulsoDetalheModalProps {
  isOpen: boolean;
  onClose: () => void;
  disparo: DisparoAvulsoLite | null;
  clientId?: number | null;
  isAdmin?: boolean;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'FILA', label: 'Fila' },
  { value: 'ENVIADO', label: 'Enviado' },
  { value: 'FALHA', label: 'Falha' },
  { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
];

const PAGE_SIZE = 50;

const statusBadgeVariant = (status: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
  switch (status) {
    case 'ENVIADO':
      return 'default';
    case 'FALHA':
    case 'CONTATO_INVALIDO':
      return 'destructive';
    case 'CANCELADO':
      return 'secondary';
    default:
      return 'outline';
  }
};

const DisparoAvulsoDetalheModal: React.FC<DisparoAvulsoDetalheModalProps> = ({
  isOpen,
  onClose,
  disparo,
  clientId,
  isAdmin,
}) => {
  const [contatos, setContatos] = useState<ContatoLite[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadContatos = async (targetPage: number) => {
    if (!disparo) return;
    const cid = clientId ?? disparo.client_id ?? undefined;
    setLoading(true);
    try {
      const res = await api.disparoAvulsoGetContatos(disparo.id, targetPage, PAGE_SIZE, cid);
      setContatos((res?.contatos as ContatoLite[]) || []);
      setTotal(res?.total ?? 0);
      setPage(targetPage);
    } catch (e: any) {
      toast({
        title: 'Erro',
        description: e?.message || 'Erro ao carregar contatos.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset + carga inicial sempre que o modal abrir para um disparo
  useEffect(() => {
    if (isOpen && disparo) {
      setStatusFilter('all');
      loadContatos(1);
    } else if (!isOpen) {
      setContatos([]);
      setTotal(0);
      setPage(1);
      setStatusFilter('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, disparo?.id]);

  const filteredContatos =
    statusFilter === 'all'
      ? contatos
      : contatos.filter((c) => c.status_envio === statusFilter);

  // Calcula a maior quantidade de variáveis para gerar colunas Var1..VarN dinamicamente
  const maxVars = filteredContatos.reduce(
    (acc, c) => Math.max(acc, Array.isArray(c.variaveis) ? c.variaveis.length : 0),
    0
  );

  if (!disparo) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Disparo Avulso #{disparo.id}</DialogTitle>
            <DialogDescription>
              {disparo.template_name || 'Template'} ·{' '}
              {disparo.whatsappConnection?.name || 'Conexão'} · Status:{' '}
              <Badge variant={statusBadgeVariant(disparo.status || '')} className="ml-1">
                {disparo.status || '-'}
              </Badge>
            </DialogDescription>
          </DialogHeader>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{disparo.total_contatos ?? 0}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{disparo.enviados ?? 0}</div>
              <div className="text-sm text-gray-600">Enviados</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{disparo.falhas ?? 0}</div>
              <div className="text-sm text-gray-600">Falhas</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {Math.max(
                  0,
                  (disparo.total_contatos ?? 0) -
                    (disparo.enviados ?? 0) -
                    (disparo.falhas ?? 0)
                )}
              </div>
              <div className="text-sm text-gray-600">Pendentes</div>
            </div>
          </div>

          {/* Metadados extras (admin) */}
          {isAdmin && disparo.client && (
            <div className="text-xs text-muted-foreground">
              Cliente: {disparo.client.trade_name || disparo.client.legal_name || `#${disparo.client_id}`}
            </div>
          )}

          {/* Toolbar: filtro + ações */}
          <div className="flex flex-wrap items-end justify-between gap-3 pt-2 border-t">
            <div className="flex items-end gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Filtrar por status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadContatos(page)}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
            <Button onClick={() => setShowExport(true)} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Exportar Disparo
            </Button>
          </div>

          {/* Tabela de contatos */}
          <div className="flex-1 overflow-auto min-h-0 border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Carregando contatos...</span>
              </div>
            ) : filteredContatos.length === 0 ? (
              <p className="text-center text-muted-foreground py-10">
                Nenhum contato com o filtro aplicado.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[160px]">Telefone</TableHead>
                    <TableHead className="min-w-[140px]">Status</TableHead>
                    <TableHead className="min-w-[180px]">Enviado em</TableHead>
                    {Array.from({ length: maxVars }, (_, i) => (
                      <TableHead key={i} className="min-w-[120px]">
                        Var {i + 1}
                      </TableHead>
                    ))}
                    <TableHead className="min-w-[200px]">Motivo Rejeição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContatos.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-gray-500" />
                          <span className="text-sm">{c.telefone_destino}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(c.status_envio)}>{c.status_envio}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.enviado_at ? new Date(c.enviado_at).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                      {Array.from({ length: maxVars }, (_, i) => (
                        <TableCell key={i} className="text-sm text-muted-foreground">
                          {c.variaveis?.[i] ?? '-'}
                        </TableCell>
                      ))}
                      <TableCell className="text-sm text-muted-foreground">
                        {c.motivo_rejeicao || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Paginação */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-4 pt-2 border-t">
              <p className="text-sm text-muted-foreground">
                Página {page} de {totalPages} ({total} contatos no total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => loadContatos(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => loadContatos(page + 1)}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ExportarDisparoAvulsoModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        disparo={disparo}
        clientId={clientId ?? disparo.client_id ?? null}
      />
    </>
  );
};

export default DisparoAvulsoDetalheModal;
